import { debugPerformance, perfNow } from './performances';
import { isPrimitive, type JSPrimitive } from './types';

/**
 * Memoize multiple methods on a class.
 *
 * ```typescript
 * class User extends Record({ firstName: '', lastName: '' }) {
 *  public getDisplayName() {
 *      return this.firstName + this.lastName
 *  }
 * }
 * memoizeClassMethods(User, ['getDisplayName'])
 * ```
 */
export function memoizeClassMethods(objClass: any, methods: Array<string | [string, string[]]>) {
    methods.forEach((method) => {
        if (Array.isArray(method)) {
            memoizeClassMethod(objClass, method[0], method[1]);
        } else {
            memoizeClassMethod(objClass, method);
        }
    });
}

/**
 * Memoize a method on a class.
 *
 * ```typescript
 * class User extends Record({ firstName: '', lastName: '' }) {
 *  public getDisplayName() {
 *      return this.firstName + this.lastName
 *  }
 * }
 * memoizeClassMethod(User, 'getDisplayName')
 * ```
 */
export function memoizeClassMethod(objClass: any, method: string, dependencies: string[] = []) {
    if (!objClass.prototype[method]) {
        return;
    }
    objClass.prototype[method] = memoizeFunction(
        objClass.prototype[method],
        dependencies,
        `${objClass.name}.${method}`
    );
}

/**
 * Memoize a method in a record.
 *
 * A narrowed set of property names can be passed as a second argument to memoize only based on these values.
 * Be careful that if a property used in the function is missing, it might create an unexpected side-effect.
 *
 * ```typescript
 * class User extends Record({ firstName: '', lastName: '' }) {
 *  public getDisplayName = memoize(() => {
 *      return this.firstName + this.lastName
 *  })
 * }
 * ```
 */
export function memoizeFunction<Args extends any[], Result>(
    fn: (...args: Args) => Result,
    dependencies: string[] = [],
    functionName: string = fn.name
): (...args: Args) => Result {
    const cache = new Map();

    const functionPerf = {
        count: 0,
        cachedCount: 0,
        missCount: 0,
        totalTime: 0,
        totalCachedTime: 0,
        cacheValueCount: 0,
        cacheValueOverwriteCount: 0,
        cacheNodeCount: 0,
        primitiveCacheNodeCount: 0,
        weakCacheNodeCount: 0,
        weakStoreCount: 0,
        heapUsedAtFirstCacheValue: undefined as number | undefined,
        peakHeapUsed: undefined as number | undefined,
        peakHeapDelta: undefined as number | undefined,
    };
    debugPerformance.memoizeFunctions[functionName] = functionPerf;

    return function (...args) {
        // @ts-ignore
        const keys = dependencies.length ? dependencies.map((key) => this[key]) : [this];
        keys.push(...args);

        debugPerformance.memoizeCount++;
        functionPerf.count++;

        const startCache = perfNow();
        const cached = getIn(cache, keys);
        if (cached !== NOSET) {
            debugPerformance.memoizeCachedCount++;
            functionPerf.cachedCount++;
            const endCache = perfNow();
            functionPerf.totalCachedTime += endCache - startCache;
            return cached;
        }

        const start = perfNow();
        functionPerf.missCount++;
        // @ts-ignore
        const result = fn.apply(this, args);
        const end = perfNow();

        functionPerf.totalTime += end - start;

        setIn(cache, keys, result, functionPerf);

        return result;
    };
}

const UNDEFINED = Symbol('undefined');
const NULL = Symbol('null');

const WEAP_STORE_KEY = Symbol('WEAP_STORE_KEY');
const VALUE_KEY = Symbol('VALUE_KEY');
const NOSET = Symbol('NOSET');
const DEBUG = Boolean(process.env.GITBOOK_DEBUG) && process.env.GITBOOK_DEBUG === 'normalization';

type MemoizeCache = Map<
    JSPrimitive | typeof WEAP_STORE_KEY | typeof VALUE_KEY,
    typeof UNDEFINED | MemoizeCache | WeakMap<any, MemoizeCache>
>;

function getIn(cache: MemoizeCache, keys: any[]): any | typeof NOSET {
    let current: MemoizeCache | undefined = cache;

    for (let i = 0; i < keys.length; i++) {
        let key = keys[i];

        if (key === undefined) {
            key = UNDEFINED;
        } else if (key === null) {
            key = NULL;
        }

        if (isPrimitive(key)) {
            current = current.get(key) as MemoizeCache;
        } else {
            if (!current.has(WEAP_STORE_KEY)) {
                return NOSET;
            }

            const cache = current.get(WEAP_STORE_KEY) as MemoizeCache;
            current = cache.get(key) as MemoizeCache;
        }

        if (!current) {
            return NOSET;
        }
    }

    const result = current.get(VALUE_KEY);
    if (result === undefined) {
        return NOSET;
    }

    return result === UNDEFINED ? undefined : result;
}

function setIn(
    cache: MemoizeCache,
    keys: any[],
    input: any,
    functionPerf: {
        cacheValueCount: number;
        cacheValueOverwriteCount: number;
        cacheNodeCount: number;
        primitiveCacheNodeCount: number;
        weakCacheNodeCount: number;
        weakStoreCount: number;
        heapUsedAtFirstCacheValue?: number;
        peakHeapUsed?: number;
        peakHeapDelta?: number;
    }
): void {
    const value = input === undefined ? UNDEFINED : input;
    let current = cache;

    for (let i = 0; i < keys.length; i++) {
        let key = keys[i];

        if (key === undefined) {
            key = UNDEFINED;
        } else if (key === null) {
            key = NULL;
        }

        if (isPrimitive(key)) {
            if (current.has(key)) {
                current = current.get(key) as MemoizeCache;
            } else {
                const cache = new Map() as MemoizeCache;
                current.set(key, cache);
                functionPerf.cacheNodeCount++;
                functionPerf.primitiveCacheNodeCount++;
                current = cache;
            }
        } else {
            let weakMap: WeakMap<any, MemoizeCache>;
            if (current.has(WEAP_STORE_KEY)) {
                weakMap = current.get(WEAP_STORE_KEY) as WeakMap<any, MemoizeCache>;
            } else {
                weakMap = new WeakMap<any, MemoizeCache>();
                current.set(WEAP_STORE_KEY, weakMap);
                functionPerf.weakStoreCount++;
            }

            if (weakMap.has(key)) {
                current = weakMap.get(key) as MemoizeCache;
            } else {
                const cache = new Map() as MemoizeCache;
                weakMap.set(key, cache);
                functionPerf.cacheNodeCount++;
                functionPerf.weakCacheNodeCount++;
                current = cache;
            }
        }
    }

    if (current.has(VALUE_KEY)) {
        functionPerf.cacheValueOverwriteCount++;
    } else {
        functionPerf.cacheValueCount++;
        // process not available in browser
        if (DEBUG && typeof process !== 'undefined') {
            const heapUsed = process.memoryUsage().heapUsed;
            if (functionPerf.heapUsedAtFirstCacheValue === undefined) {
                functionPerf.heapUsedAtFirstCacheValue = heapUsed;
                functionPerf.peakHeapUsed = heapUsed;
                functionPerf.peakHeapDelta = 0;
            } else {
                functionPerf.peakHeapUsed = Math.max(
                    functionPerf.peakHeapUsed ?? heapUsed,
                    heapUsed
                );
                functionPerf.peakHeapDelta = Math.max(
                    functionPerf.peakHeapDelta ?? 0,
                    heapUsed - functionPerf.heapUsedAtFirstCacheValue
                );
            }
        }
    }
    current.set(VALUE_KEY, value);
}
